import type { EcState } from '@packages/shared/src/utils/ecState/ecState';
import { Audiences } from '../../interfaces/components';

/**
 * Returns the main audience group of a customer based on the EcState data.
 *
 * @param {EcState} ecState - The EcState data (by cookie "ecState") containing customer information.
 * @returns {string} The main audience group of the customer.
 */
export const getMainAudience = (ecState?: EcState): Audiences => {
  if (!ecState?.loggedIn) {
    return Audiences.anonymous;
  }
  if (ecState.creditAffinity) {
    return Audiences.creditAffinity;
  }
  if (ecState.bonusProgram) {
    return Audiences.bonusProgram;
  }

  return Audiences.loggedIn;
};

/**
 * Returns an array containing all the audiences the customer is in, based on the EcState data.
 *
 * @param {EcState} ecState - The EcState data (by cookie "ecState") containing customer information.
 * @param {Audiences[]} [previewAudiences] - When set (CMS preview mode), overrides the EcState-derived
 * audiences with the audiences selected in the preview bar.
 * @returns {string[]} An array of all the audiences the customer is in.
 */
export const getAllAudiences = (ecState?: EcState, previewAudiences?: Audiences[]): Audiences[] => {
  if (previewAudiences) {
    return previewAudiences;
  }

  if (!ecState?.loggedIn) {
    return [Audiences.anonymous];
  }

  const result = [Audiences.loggedIn];

  if (ecState.bonusProgram) {
    result.push(Audiences.bonusProgram);
  }
  if (ecState.creditAffinity) {
    result.push(Audiences.creditAffinity);
  }
  return result;
};

/**
 * Parses a comma-seperated list of audiences onto the ecState object
 * @param param comma-seperated list of audiences (e.g. "logged in,creditAffinity")
 * @returns ecState object
 */
export const getEcStateFromParams = (param = ''): EcState => {
  if (!param) {
    return { loggedIn: false };
  }

  const audiences = new Set(param.split(','));
  if (!audiences.has(Audiences.loggedIn) || audiences.has(Audiences.anonymous)) {
    return { loggedIn: false };
  }

  return {
    loggedIn: true,
    bonusProgram: audiences.has(Audiences.bonusProgram),
    creditAffinity: audiences.has(Audiences.creditAffinity),
  };
};
